import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ProtectedLink } from '../context/AuthGateContext';
import {
  Facebook, Twitter, Instagram, Linkedin,
  Mail, Phone, ArrowRight
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const footerSections = {
    company: {
      title: 'Company',
      links: [
        { label: 'About Us', href: '/about' },
        { label: 'How It Works', href: '/how-it-works' },
        { label: 'Careers', href: '/careers' },
        { label: 'Partner With Us', href: '/partner' }
      ]
    },
    students: {
      title: 'For Students',
      links: [
        { label: 'Find Accommodation', href: '/' },
        { label: 'Student Guide', href: '/student-guide' },
        { label: 'City Guides', href: '/city-guides' },
        { label: 'University Partnerships', href: '/universities' },
        { label: 'Student Resources', href: '/resources' }
      ]
    },
    landlords: {
      title: 'For Landlords',
      links: [
        { label: 'List Your Property', href: '/list-property' },
        { label: 'Property Management', href: '/property-management' },
        { label: 'Landlord Resources', href: '/landlord-resources' },
        { label: 'Pricing', href: '/pricing' },
        { label: 'Success Stories', href: '/success-stories' }
      ]
    },
    support: {
      title: 'Support',
      links: [
        { label: 'Help Center', href: '/help' },
        { label: 'Contact Us', href: '/contact' },
        { label: 'Safety & Security', href: '/safety' },
        { label: 'Trust & Safety', href: '/trust-safety' },
        { label: 'Community Guidelines', href: '/guidelines' }
      ]
    }
  };

  return (
    <footer className="bg-gray-900 text-white">

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">

          {/* Brand + Newsletter Section */}
          <div className="lg:col-span-2">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-0 mb-5">
              <span className="text-2xl font-black tracking-tight text-white">Rental</span>
              <img
                src="/rentalx-x-new.svg"
                alt="X"
                style={{ height: '1.6rem', width: 'auto', marginLeft: '0.05em', marginBottom: '0.02em', verticalAlign: 'middle' }}
              />
            </Link>

            <p className="text-gray-300 mb-6 leading-relaxed text-sm">
              Making student accommodation simple, safe, and affordable.
              Your home away from home is just a click away.
            </p>

            {/* Contact Info */}
            <div className="space-y-2 mb-6">
              <div className="flex items-center text-gray-300 text-sm">
                <Phone className="w-4 h-4 mr-3 text-[#2cbdc4] flex-shrink-0" />
                <span>+61 1800 RENTAL</span>
              </div>
              <div className="flex items-center text-gray-300 text-sm">
                <Mail className="w-4 h-4 mr-3 text-[#2cbdc4] flex-shrink-0" />
                <a href="mailto:hello@rentalx.io" className="hover:text-white transition-colors">
                  hello@rentalx.io
                </a>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-2 mb-8">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white hover:bg-white/10 p-2 rounded-lg">
                <Facebook className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white hover:bg-white/10 p-2 rounded-lg">
                <Twitter className="w-5 h-5" />
              </Button>
              <a
                href="https://www.instagram.com/rentalx.io?igsh=MTNzMTk0aHppcWs3OA%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-9 h-9 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                aria-label="RentalX on Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white hover:bg-white/10 p-2 rounded-lg">
                <Linkedin className="w-5 h-5" />
              </Button>
            </div>

            {/* Newsletter — compact horizontal row */}
            <div className="border-t border-white/10 pt-5 mt-2">
              {subscribed ? (
                <p className="text-sm text-[#2cbdc4] font-semibold">✓ Subscribed! Thanks for joining.</p>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-gray-400 whitespace-nowrap font-medium">Stay updated:</p>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && email && setSubscribed(true)}
                    placeholder="Your email"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 h-8 text-xs flex-1 focus:border-[#2cbdc4]"
                  />
                  <Button
                    onClick={() => { if (email) setSubscribed(true); }}
                    className="h-8 px-3 text-xs font-semibold flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #2cbdc4, #094f6f)' }}
                  >
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Footer Links */}
          {Object.entries(footerSections).map(([key, section]) => (
            <div key={key}>
              <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wide">{section.title}</h4>
              <ul className="space-y-2.5">
                {section.links.map((link, index) => (
                  <li key={index}>
                    <ProtectedLink
                      to={link.href}
                      className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                    >
                      {link.label}
                    </ProtectedLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-500 text-sm">
              © {currentYear} RentalX. All rights reserved.
            </div>
            <div className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-2 text-sm">
              <Link to="/privacy" className="text-gray-500 hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-500 hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/cookies" className="text-gray-500 hover:text-white transition-colors">Cookie Policy</Link>
              <Link to="/accessibility" className="text-gray-500 hover:text-white transition-colors">Accessibility</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
